/**
 * PKCE OAuth2 client for rivyoo-server's existing partner SSO provider.
 * See rivyoo-server `docs/features/SSO_OAUTH_PROVIDER.md`.
 *
 * Flow: buildAuthorizeUrl() -> browser redirect through Google -> callback
 * with ?code&state -> exchangeCodeForToken() -> fetchProfile().
 *
 * Requires a public OAuth2 `Application` to be registered on rivyoo-server
 * (PKCE, no client secret, redirect_uris including this site's
 * `/auth/callback`); see website-new/.env.example for the required config.
 */

import { getApiBaseUrl } from "@/lib/api/config";

export { getApiBaseUrl };

const PKCE_VERIFIER_KEY = "rivyou_oauth_pkce_verifier";
const OAUTH_STATE_KEY = "rivyou_oauth_state";
const OAUTH_RETURN_TO_KEY = "rivyou_oauth_return_to";

export function getOAuthClientId() {
  return process.env.REACT_APP_RIVYOO_OAUTH_CLIENT_ID || "";
}

export function getRedirectUri() {
  return `${window.location.origin}/auth/callback`;
}

function randomString(length = 64) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  const bytes = new Uint8Array(length);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => chars[b % chars.length]).join("");
}

function base64UrlEncode(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

async function generateCodeChallenge(verifier) {
  const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64UrlEncode(digest);
}

/**
 * Builds the `/oauth/authorize/` URL and stashes the PKCE verifier + state
 * in sessionStorage so `exchangeCodeForToken` can complete the handshake
 * after the redirect back from rivyoo-server.
 */
export async function buildAuthorizeUrl({ returnTo = "/" } = {}) {
  const clientId = getOAuthClientId();
  if (!clientId) {
    throw new Error(
      "REACT_APP_RIVYOO_OAUTH_CLIENT_ID is not set. Register a public OAuth2 Application on rivyoo-server first (see .env.example).",
    );
  }

  const verifier = randomString(64);
  const state = randomString(32);
  const challenge = await generateCodeChallenge(verifier);

  window.sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  window.sessionStorage.setItem(OAUTH_STATE_KEY, state);
  window.sessionStorage.setItem(OAUTH_RETURN_TO_KEY, returnTo);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: getRedirectUri(),
    scope: "profile",
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });

  return `${getApiBaseUrl()}/oauth/authorize/?${params.toString()}`;
}

/** Starts the Sign Up / Sign In flow by redirecting the browser to rivyoo-server. */
export async function startSignIn({ returnTo = window.location.pathname } = {}) {
  const url = await buildAuthorizeUrl({ returnTo });
  window.location.assign(url);
}

/**
 * Completes the flow after the browser lands back on `/auth/callback`.
 * Validates `state`, exchanges `code` for a token, and returns
 * `{ accessToken, refreshToken, returnTo }`.
 */
export async function completeSignIn({ code, state }) {
  const expectedState = window.sessionStorage.getItem(OAUTH_STATE_KEY);
  const verifier = window.sessionStorage.getItem(PKCE_VERIFIER_KEY);
  const returnTo = window.sessionStorage.getItem(OAUTH_RETURN_TO_KEY) || "/";

  window.sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  window.sessionStorage.removeItem(OAUTH_STATE_KEY);
  window.sessionStorage.removeItem(OAUTH_RETURN_TO_KEY);

  if (!state || !expectedState || state !== expectedState) {
    throw new Error("OAuth state mismatch. Please try signing in again.");
  }
  if (!verifier) {
    throw new Error("Missing PKCE verifier. Please try signing in again.");
  }

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: getRedirectUri(),
    client_id: getOAuthClientId(),
    code_verifier: verifier,
  });

  const res = await fetch(`${getApiBaseUrl()}/oauth/token/`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });

  if (!res.ok) {
    throw new Error(`Token exchange failed (${res.status})`);
  }

  const data = await res.json();
  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    returnTo,
  };
}

/** Fetches the signed-in partner profile via `/api/partner/profile/`. */
export async function fetchProfile(accessToken) {
  const res = await fetch(`${getApiBaseUrl()}/api/partner/profile/`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });

  if (!res.ok) {
    throw new Error(`Fetching profile failed (${res.status})`);
  }

  return res.json();
}
